import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Store, User, Lock } from 'lucide-react';
import { vendorApi } from '../../api/vendorApi';
import LocationPicker from '@/features/locations/components/LocationPicker';
import PickupLocationSection from '@/features/locations/components/PickupLocationSection';

const HISTORY_MAX_LENGTH = 5000;

export default function VendorProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);

  // === [ADDED] State for B2B Toggle ===
  const [isB2BEnabled, setIsB2BEnabled] = useState(false);
  const [b2bUpdating, setB2bUpdating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await vendorApi.getProfile();
        setProfile(data);
        setIsB2BEnabled(data.is_b2b_enabled || false);
        setForm({
          first_name: data.first_name ?? '',
          last_name: data.last_name ?? '',
          email: data.email ?? '',
          address: data.address ?? '',
          postal_code: data.postal_code ?? '',
          profile_picture: data.profile_picture ?? '',
          location_id: data.location_id ?? null,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          pickup_location_id: data.pickup_location_id ?? null,
          pickup_latitude: data.pickup_latitude ?? null,
          pickup_longitude: data.pickup_longitude ?? null,
          pickup_address: data.pickup_address ?? '',
          vendor_local_phone: data.vendor_local_phone ?? '',
          vendor_shop_name_fa: data.vendor_shop_name_fa ?? '',
          vendor_shop_name_en: data.vendor_shop_name_en ?? '',
          vendor_banner_url: data.vendor_banner_url ?? '',
          vendor_history_fa: data.vendor_history_fa ?? '',
          shaba_number: data.shaba_number ?? '',
          password: '',
        });
        setState('ready');
      } catch (requestError) {
        console.error(requestError);
        setError('خطا در دریافت اطلاعات پروفایل');
        setState('error');
      }
    })();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleLocationPatch = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const handleToggleB2B = async () => {
    try {
      const newVal = !isB2BEnabled;
      setB2bUpdating(true);
      setIsB2BEnabled(newVal);
      await vendorApi.updateB2BSettings(newVal);
      if (newVal) alert('سیستم شما برای دریافت قراردادهای سازمانی (B2B) فعال شد و در لیست شرکت‌ها نمایش داده می‌شوید.');
      else alert('دریافت قراردادهای سازمانی غیرفعال شد.');
    } catch {
      setIsB2BEnabled(!isB2BEnabled); // Revert on error
      alert('خطا در تغییر تنظیمات B2B');
    } finally {
      setB2bUpdating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSuccess(false);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.email) delete payload.email;
      const updated = await vendorApi.updateProfile(payload);
      setProfile(updated);
      setSuccess(true);
    } catch (requestError) {
      console.error(requestError);
      setError('ذخیره پروفایل با خطا مواجه شد');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBannerUploading(true);
    setSuccess(false);
    setError('');
    try {
      const updated = await vendorApi.uploadBanner(file);
      setProfile(updated);
      setForm((current) => ({ ...current, vendor_banner_url: updated.vendor_banner_url ?? '' }));
      setSuccess(true);
    } catch (requestError) {
      console.error(requestError);
      setError('بارگذاری بنر با خطا مواجه شد');
    } finally {
      setBannerUploading(false);
      event.target.value = '';
    }
  };

  if (state === 'loading') return <p className="admin-page-subtitle">در حال دریافت اطلاعات...</p>;
  if (state === 'error') return <div className="admin-table-error"><AlertCircle size={16} />{error}</div>;

  return (
      <div>
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">پروفایل من</h1>
            <p className="admin-page-subtitle">اطلاعات فروشگاه و حساب کاربری شما</p>
          </div>
        </div>

        {profile && (
            <div className="card admin-profile-card" style={{ marginBottom: 20 }}>
              <dl>
                <div><dt>وضعیت تایید</dt><dd>{profile.is_verified ? 'تایید شده' : 'در انتظار تایید'}</dd></div>
                <div><dt>نرخ کمیسیون ایرانچارسوق</dt><dd>{profile.commission_rate}%</dd></div>
              </dl>
            </div>
        )}

        {success && (
            <div className="admin-profile-success">
              <CheckCircle2 size={16} /> اطلاعات با موفقیت ذخیره شد.
            </div>
        )}

        <form className="card admin-profile-form" onSubmit={handleSubmit}>
          <div className="admin-profile-section">
            <h2><User size={16} /> اطلاعات شخصی</h2>
            <div className="admin-profile-form-grid">
              <label>نام<input name="first_name" value={form.first_name} onChange={handleChange} /></label>
              <label>نام خانوادگی<input name="last_name" value={form.last_name} onChange={handleChange} /></label>
              <label>ایمیل<input name="email" type="email" value={form.email} onChange={handleChange} /></label>
              <label>کد پستی<input name="postal_code" value={form.postal_code} onChange={handleChange} /></label>
              <label>عکس پروفایل (URL)<input name="profile_picture" value={form.profile_picture} onChange={handleChange} /></label>
            </div>
            <div className="admin-profile-wide-field">
              <label>آدرس<textarea name="address" value={form.address} onChange={handleChange} /></label>
            </div>

            <LocationPicker
                value={{
                  location_id: form.location_id,
                  latitude: form.latitude,
                  longitude: form.longitude,
                  address: form.address,
                }}
                onChange={handleLocationPatch}
            />
          </div>

          <PickupLocationSection form={form} setForm={setForm} />

          <div className="admin-profile-section">
            <h2><Store size={16} /> اطلاعات فروشگاه</h2>

            {/* === [ADDED] B2B Toggle Section === */}
            <div style={{ background: isB2BEnabled ? '#f0fdf4' : '#f8fafc', border: isB2BEnabled ? '1px solid #10b981' : '1px solid #cbd5e1', padding: '1rem 1.5rem', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: '0 0 0.3rem 0', color: isB2BEnabled ? '#065f46' : '#1e293b', fontSize: '1rem' }}>پذیرش قراردادهای سازمانی (B2B)</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>با فعال‌سازی این گزینه، شرکت‌ها می‌توانند برای کالاهای شما درخواست تخفیف عمده ثبت کنند.</p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: b2bUpdating ? 'not-allowed' : 'pointer', gap: '0.5rem', opacity: b2bUpdating ? 0.6 : 1 }}>
                <span style={{ fontWeight: 'bold', color: isB2BEnabled ? '#15803d' : '#64748b' }}>{isB2BEnabled ? 'فعال' : 'غیرفعال'}</span>
                <div style={{ width: '50px', height: '26px', background: isB2BEnabled ? '#10b981' : '#cbd5e1', borderRadius: '13px', position: 'relative', transition: 'all 0.3s' }} onClick={b2bUpdating ? undefined : handleToggleB2B}>
                  <div style={{ width: '22px', height: '22px', background: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', right: isB2BEnabled ? '2px' : '26px', transition: 'all 0.3s' }} />
                </div>
              </label>
            </div>
            {/* ================================= */}

            <div className="vendor-banner-upload">
              {profile.vendor_banner_url ? (
                  <img src={profile.vendor_banner_url} alt={profile.vendor_shop_name_fa} className="vendor-banner-preview" />
              ) : (
                  <div className="vendor-banner-placeholder">بنر فروشگاه هنوز بارگذاری نشده است</div>
              )}
              <label className="vendor-file-label">
                {bannerUploading ? 'در حال بارگذاری...' : 'بارگذاری بنر فروشگاه'}
                <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleBannerChange}
                    disabled={bannerUploading}
                />
              </label>
            </div>
            <div className="admin-profile-form-grid">
              <label>تلفن محلی فروشگاه<input name="vendor_local_phone" value={form.vendor_local_phone} onChange={handleChange} maxLength={10} /></label>
              <label>نام فروشگاه (فارسی)<input name="vendor_shop_name_fa" value={form.vendor_shop_name_fa} onChange={handleChange} /></label>
              <label>نام لاتین فروشگاه برای جست‌وجو<input name="vendor_shop_name_en" value={form.vendor_shop_name_en} onChange={handleChange} /></label>
              <label>بنر فروشگاه (URL)<input name="vendor_banner_url" value={form.vendor_banner_url} onChange={handleChange} /></label>
              <label>شماره شبا<input name="shaba_number" value={form.shaba_number} onChange={handleChange} maxLength={26} /></label>
            </div>
            <div className="admin-profile-wide-field">
              <label>
                تاریخچه فروشگاه
                <textarea
                    name="vendor_history_fa"
                    value={form.vendor_history_fa}
                    onChange={handleChange}
                    maxLength={HISTORY_MAX_LENGTH}
                    rows={6}
                    placeholder="داستان و سابقه کسب‌وکار خود را برای مشتریان بنویسید..."
                />
              </label>
              <small>
                {form.vendor_history_fa.length.toLocaleString('fa-IR')} / {HISTORY_MAX_LENGTH.toLocaleString('fa-IR')}
              </small>
            </div>
          </div>

          <div className="admin-profile-section">
            <h2><Lock size={16} /> امنیت</h2>
            <div className="admin-profile-password">
              <label>رمز عبور جدید<input name="password" type="password" value={form.password} onChange={handleChange} placeholder="خالی بگذارید تا تغییر نکند" /></label>
            </div>
          </div>

          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
        </form>
      </div>
  );
}